import React, { useState, useEffect } from "react";
import { Row, Col, Button, Modal, Form, Badge } from "react-bootstrap";
import { FaEdit, FaPlus, FaTrash } from "react-icons/fa";
import ResponsiveTable from "./ResponsiveTable";
import ticketService from "../services/ticketService";
import { toast } from "react-toastify";
import Spinner from "./Spinner";

const TicketTypes = () => {
  // State
  const [ticketTypes, setTicketTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    description: "",
    isActive: true
  });

  // Load ticket types on component mount
  useEffect(() => {
    fetchTicketTypes();
  }, []);

  // Fetch ticket types
  const fetchTicketTypes = async () => {
    try {
      setLoading(true);
      const data = await ticketService.getTicketTypes();
      setTicketTypes(data);
    } catch (error) {
      toast.error("Failed to load ticket types");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Handle opening the create modal
  const handleCreateModal = () => {
    setFormData({
      id: null,
      name: "",
      description: "",
      isActive: true
    });
    setEditMode(false);
    setShowModal(true);
  };

  // Handle opening the edit modal for a ticket type
  const handleEditModal = (ticketType) => {
    setFormData({
      id: ticketType.id,
      name: ticketType.name,
      description: ticketType.description || "",
      isActive: ticketType.isActive
    });
    setEditMode(true);
    setShowModal(true);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value
    });
  };

  // Handle saving a ticket type (create or update)
  const handleSave = async (e) => {
    e.preventDefault();
    try {
      // Prepare data for API
      const ticketTypeData = {
        ...formData
      };

      if (editMode) {
        const updatedTicketType = await ticketService.updateTicketType(ticketTypeData.id, ticketTypeData);
        // Update local state directly
        setTicketTypes(prevTypes => 
          prevTypes.map(type => 
            type.id === updatedTicketType.id ? updatedTicketType : type
          )
        );
        toast.success("Ticket type updated successfully");
      } else {
        const newTicketType = await ticketService.createTicketType(ticketTypeData);
        // Add new ticket type to local state
        setTicketTypes(prevTypes => [...prevTypes, newTicketType]);
        toast.success("Ticket type created successfully");
      }
      
      setShowModal(false);
    } catch (error) {
      toast.error(
        error.response?.data?.message || 
        `Failed to ${editMode ? "update" : "create"} ticket type`
      );
    }
  };

  // Handle deleting a ticket type
  const handleDelete = async (ticketTypeId) => {
    if (window.confirm("Are you sure you want to delete this ticket type?")) {
      try {
        await ticketService.deleteTicketType(ticketTypeId);
        // Remove from local state
        setTicketTypes(prevTypes => prevTypes.filter(type => type.id !== ticketTypeId));
        toast.success("Ticket type deleted successfully");
      } catch (error) {
        console.log("Error response:", error.response);
        const errorMessage = error.response?.data?.message || error.response?.data || "Failed to delete ticket type";
        toast.error(errorMessage);
      }
    }
  };

  // Handle toggling active status
  const handleToggleActive = async (ticketType) => {
    try {
      const updatedTicketType = {
        ...ticketType,
        isActive: !ticketType.isActive
      };
      const response = await ticketService.updateTicketType(ticketType.id, updatedTicketType);
      // Update local state directly
      setTicketTypes(prevTypes =>
        prevTypes.map(type =>
          type.id === ticketType.id ? response : type
        )
      );
      toast.success(`Ticket type ${updatedTicketType.isActive ? "activated" : "deactivated"} successfully`);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update ticket type status"
      );
    }
  };

  // Table columns configuration
  const columns = [
    { key: "name", label: "Name", width: "25%" },
    { key: "description", label: "Description", width: "50%" },
    { 
      key: "isActive", 
      label: "Status", 
      width: "15%",
      render: (isActive) => (
        <Badge bg={isActive ? "success" : "danger"}>
          {isActive ? "Active" : "Inactive"}
        </Badge>
      ) 
    }
  ];

  // Table actions configuration
  const actions = [
    {
      label: "Edit",
      icon: <FaEdit />,
      variant: "outline-primary",
      onClick: handleEditModal
    },
    {
      label: "Toggle Status",
      icon: null,
      variant: "outline-warning",
      onClick: handleToggleActive,
      render: (ticketType) => (
        <Button 
          variant={ticketType.isActive ? "outline-warning" : "outline-success"}
          size="sm"
          onClick={() => handleToggleActive(ticketType)}
        >
          {ticketType.isActive ? "Deactivate" : "Activate"}
        </Button>
      )
    },
    {
      label: "Delete",
      icon: <FaTrash />,
      variant: "outline-danger",
      onClick: (ticketType) => handleDelete(ticketType.id)
    }
  ];

  return (
    <div className="container mt-4">
      <Row className="mb-4">
        <Col md={6}>
          <Button variant="primary" onClick={handleCreateModal}>
            <FaPlus className="me-1" /> Add Ticket Type
          </Button>
        </Col>
        <Col md={6}>
        </Col>
      </Row>

      {loading ? (
        <Spinner />
      ) : ticketTypes.length === 0 ? (
        <p>No ticket types found. Create one to get started.</p>
      ) : (
        <ResponsiveTable
          data={ticketTypes}
          columns={columns}
          actions={actions}
        />
      )}

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editMode ? "Edit" : "Create"} Ticket Type</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter ticket type name"
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter description for this ticket type"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
              />
              <Form.Text className="text-muted">
                Only active ticket types can be selected for new tickets
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editMode ? "Update" : "Create"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default TicketTypes;
